import { useEffect, useState } from "react";
import { Search, Loader2, Archive, Trash2, ArchiveRestore, Pencil } from "lucide-react";
import { toast } from "sonner";

import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/auth-context";
import { formatUrgency } from "@/lib/scores";
import CaseEditDialog from "@/components/CaseEditDialog";

export default function AdminCases() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const openEdit = async (c) => {
    // Re-fetch the full case so the dialog has docs + admin_notes (the list
    // endpoint returns a trimmed view for performance).
    try {
      const { data } = await api.get(`/cases/${c.id}`);
      setSelected(data);
      setEditOpen(true);
    } catch (e) {
      // Fallback to the row data we already have if the detail fetch fails
      setSelected(c);
      setEditOpen(true);
    }
  };

  const onCaseSaved = (updated) => {
    setSelected(updated);
    setCases((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/cases", { params: q ? { q } : {} });
      setCases(data);
    } finally {
      setLoading(false);
    }
  };

  // Permanently delete a case. On success, remove it from local state so the
  // row disappears immediately without a full re-fetch. On failure, surface
  // the backend reason via toast.
  const del = async (c) => {
    try {
      await api.delete(`/admin/cases/${c.id}`);
      setCases((prev) => prev.filter((x) => x.id !== c.id));
      toast.success("Case deleted permanently");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete case");
    }
  };

  // Toggle archive flag and reconcile local state.
  const archive = async (c) => {
    try {
      if (c.is_archived) {
        await api.post(`/admin/cases/${c.id}/unarchive`);
      } else {
        await api.post(`/admin/cases/${c.id}/archive`);
      }
      setCases((prev) =>
        prev.map((x) => (x.id === c.id ? { ...x, is_archived: !c.is_archived } : x))
      );
      toast.success(c.is_archived ? "Case unarchived" : "Case archived");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update case");
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  return (
    <div data-testid="admin-cases" className="p-6 lg:p-10 max-w-7xl">
      <p className="text-xs tracking-[0.22em] uppercase font-semibold text-[#C5A059] mb-2">
        Admin
      </p>
      <h1 className="font-heading text-3xl lg:text-4xl font-semibold text-[#0A192F]">
        Cases
      </h1>

      <div className="mt-6 max-w-md relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          data-testid="admin-cases-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search case type, summary, city…"
          className="pl-10 rounded-lg border-slate-300"
        />
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-[#0A192F]" />
          </div>
        ) : cases.length === 0 ? (
          <p className="py-16 text-center text-slate-500" data-testid="cases-empty">
            No cases yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-slate-50 text-xs uppercase tracking-[0.18em] text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3">Case</th>
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="text-left px-4 py-3">Location</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Urgency</th>
                  <th className="text-right px-4 py-3">View</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {cases.map((c) => (
                  <tr key={c.id} data-testid={`admin-case-row-${c.id}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-[#0A192F] line-clamp-1">
                        {c.summary?.executive_summary?.slice(0, 80) || "Case " + c.id.slice(0, 8)}
                      </p>
                      <p className="text-xs text-slate-500">{c.id}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">
                      {c.case_type || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">
                      {[c.city, c.state].filter(Boolean).join(", ") || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm capitalize text-slate-700">
                      {c.status}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">
                      {c.urgency_score ? `${formatUrgency(c.urgency_score)}/10` : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <Button
                          data-testid={`edit-case-${c.id}-btn`}
                          size="sm"
                          variant="outline"
                          onClick={() => openEdit(c)}
                          className="rounded-lg h-8 border-slate-300"
                        >
                          <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid={`archive-case-${c.id}-btn`}
                          onClick={() => archive(c)}
                          className="rounded-lg h-8 px-2"
                          aria-label={c.is_archived ? "Unarchive case" : "Archive case"}
                        >
                          {c.is_archived ? (
                            <ArchiveRestore className="w-4 h-4 text-[#C5A059]" />
                          ) : (
                            <Archive className="w-4 h-4 text-slate-600" />
                          )}
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              data-testid={`delete-case-${c.id}-btn`}
                              className="rounded-lg h-8 px-2 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                              aria-label="Delete case"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete this case?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This permanently removes the case and any attorney saves.
                                Use archive if you want to hide it instead.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                data-testid={`confirm-delete-${c.id}-btn`}
                                onClick={() => del(c)}
                                className="bg-rose-600 hover:bg-rose-700"
                              >
                                Delete permanently
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <CaseEditDialog
        caseData={selected}
        open={editOpen}
        onOpenChange={setEditOpen}
        onSaved={onCaseSaved}
      />
    </div>
  );
}
